import React, { useState, useCallback } from 'react';
import { Polyline, Circle, Marker } from '@react-google-maps/api';
import useLongPress from '../CustomHooks/useLongPress';
import { getVertexRadius } from '@jumbo/utils/mapHelpers';

const getRotationAngle = coords => {
  if (!coords || coords.length < 2) return 0;

  // Find last two distinct points
  const last = coords[coords.length - 1];
  for (let i = coords.length - 2; i >= 0; i--) {
    const prev = coords[i];
    if (prev.lat !== last.lat || prev.lng !== last.lng) {
      const dLng = ((last.lng - prev.lng) * Math.PI) / 180;
      const lat1Rad = (prev.lat * Math.PI) / 180;
      const lat2Rad = (last.lat * Math.PI) / 180;

      const y = Math.sin(dLng) * Math.cos(lat2Rad);
      const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

      return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
    }
  }

  return 0; // All points identical
};

const isExtractionLine = label => {
  if (!label) return false;
  const lower = label.toLowerCase();
  return lower.includes('extraction') || lower.includes('main extraction');
};

const RenderLines = ({
  lines,
  selected,
  setSelected,
  calculateLineDistance,
  currentLine,
  mode,
  setCurrentLine,
  setLines,
  onUpdateLine,
  zoom,
}) => {
  const [draggingVertex, setDraggingVertex] = useState(null);

  const { start, cancel, wasLongPress, shouldBlockDrag } = useLongPress(
    useCallback(({ lineId, vertexIdx }) => {
      setDraggingVertex({ lineId, vertexIdx });
    }, []),
  );

  // Saved line vertex drag end → optimistic update + API
  const handleSavedVertexDragEnd = useCallback(
    async (e, line, vertexIdx) => {
      const newCoords = line.coords.map((c, i) => (i === vertexIdx ? { lat: e.latLng.lat(), lng: e.latLng.lng() } : c));
      const updatedLine = { ...line, coords: newCoords };

      setLines(prev => prev.map(l => (l.id === line.id ? updatedLine : l)));
      setDraggingVertex(null);

      try {
        await onUpdateLine?.(updatedLine);
      } catch (err) {
        console.error('Failed to update line vertex:', err);
        setLines(prev => prev.map(l => (l.id === line.id ? line : l))); // rollback
      }
    },
    [setLines, onUpdateLine],
  );

  // In-progress line vertex drag end → local state only
  const handleCurrentVertexDragEnd = useCallback(
    (e, vertexIdx) => {
      setCurrentLine(prev => prev.map((c, i) => (i === vertexIdx ? { lat: e.latLng.lat(), lng: e.latLng.lng() } : c)));
      setDraggingVertex(null);
    },
    [setCurrentLine],
  );

  return (
    <>
      {/* ── Saved lines ── */}
      {lines.map((line, index) => {
        const coords = line.coords;

        console.log('arrow coords:', coords[coords.length - 2], coords[coords.length - 1]);

        const isExtraction = isExtractionLine(line.mapLabel || line.label);
        const isSelected = selected?.id === line.id;

        let arrowAngle = 0;
        if (isExtraction && coords?.length >= 2) {
          arrowAngle = getRotationAngle(coords);
        }

        return (
          <React.Fragment key={`line-${line.mapLabel}-${index}`}>
            {/* Wide invisible hitbox */}
            <Polyline
              path={coords}
              options={{
                clickable: mode === null,
                strokeColor: 'transparent',
                strokeOpacity: 0,
                strokeWeight: 20,
                zIndex: 99999,
              }}
              onClick={() =>
                mode === null &&
                setSelected({
                  ...line,
                  length: calculateLineDistance(coords),
                  mapType: 'line',
                })
              }
            />

            {/* Visible line */}
            <Polyline
              path={coords}
              options={{
                clickable: false,
                strokeColor: line.hexColor || '#0000FF',
                strokeOpacity: 1,
                strokeWeight: 4,
                zIndex: 99998,
              }}
            />

            {/* Extraction arrow */}
            {isExtraction && coords?.length >= 2 && (
              <Marker
                position={coords[coords.length - 1]}
                icon={{
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  fillColor: line.hexColor || '#FF0000',
                  fillOpacity: 1,
                  strokeColor: line.hexColor || '#FF0000',
                  strokeWeight: 2,
                  scale: 3,
                  rotation: arrowAngle,
                }}
                zIndex={100000}
              />
            )}

            {/* Vertex handles — only when selected */}
            {isSelected &&
              mode === null &&
              coords.map((coord, vIdx) => {
                const isDragging = draggingVertex?.lineId === line.id && draggingVertex?.vertexIdx === vIdx;

                return (
                  <Marker
                    key={`saved-line-vertex-${line.id}-${vIdx}`}
                    position={coord}
                    draggable={true}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: isDragging ? 9 : 6,
                      fillColor: isDragging ? '#FFD700' : '#FFFFFF',
                      fillOpacity: 1,
                      strokeColor: line.hexColor || '#0000FF',
                      strokeWeight: 2,
                    }}
                    onMouseDown={() => start({ lineId: line.id, vertexIdx: vIdx })}
                    onMouseUp={cancel}
                    onDragStart={e => {
                      if (shouldBlockDrag()) e.stop();
                    }}
                    onDragEnd={e => {
                      if (!wasLongPress()) return;
                      handleSavedVertexDragEnd(e, line, vIdx);
                    }}
                    onClick={() => {
                      if (wasLongPress()) return;
                    }}
                    options={{ zIndex: isDragging ? 1000 : 100 }}
                  />
                );
              })}
          </React.Fragment>
        );
      })}

      {/* ── In-progress line ── */}
      {currentLine.length > 1 && (
        <Polyline
          path={currentLine}
          options={{
            strokeColor: 'rgba(0, 255, 140, 0.8)',
            strokeOpacity: 0.8,
            strokeWeight: 3,
          }}
        />
      )}

      {/* ── In-progress line vertices ── */}
      {currentLine.map((coord, idx) => {
        const isDragging = draggingVertex?.lineId === 'current' && draggingVertex?.vertexIdx === idx;

        return (
          <Circle
            key={`line-point-${idx}`}
            center={coord}
            radius={getVertexRadius(zoom)}
            draggable={true}
            options={{
              strokeColor: '#fff',
              fillColor: isDragging ? '#FFD700' : 'blue',
              fillOpacity: 0.7,
              strokeWeight: 2,
              clickable: true,
              zIndex: isDragging ? 999 : 1,
            }}
            onMouseDown={() => start({ lineId: 'current', vertexIdx: idx })}
            onMouseUp={cancel}
            onDragStart={e => {
              if (shouldBlockDrag()) e.stop();
            }}
            onDragEnd={e => {
              if (!wasLongPress()) return;
              handleCurrentVertexDragEnd(e, idx);
            }}
            onClick={e => {
              if (wasLongPress()) return;
              e.stop();
              setCurrentLine(prev => prev.filter((_, i) => i !== idx));
            }}
          />
        );
      })}
    </>
  );
};

export default RenderLines;
